/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useContext, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import moment from 'moment';
import { eventUtil } from '@marriott/mi-ui-library-shop';
import { Text, Types } from '@marriott/mi-ui-library';
import { usePersistentGlobalStore } from '@marriott/mi-store-utils';
import { phoenixOfferSuggestedPlacesQuery } from '@marriott/mi-offers-graphql';
import { CN_FLEXIBLE_FORMAT_YEAR_MONTH, isGreaterChinaLocale } from '@marriott/shared/mi-helper-utils';
import { getLocalizedDate as getFormattedLocalizedDate } from '@marriott/mi-ui-library';

import { ContentContext, FormValuesContext } from '../../../../OffersSearchForm/index.contexts';
import { CALENDAR_STATE, DESTINATION_STATE } from '../../../../OffersSearchForm/index.constants';
import { DATES, DESTINATION, DESTINATION_HIDDEN_FIELDS } from '../../../../OffersSearchForm/constants/StoreConstants';
import { UXL_ERROR_POLICY } from '../../../../../api/apiConstants';
import { setDestination, setDates } from '../../../../OffersSearchForm/store/store.actions';
import { getMPOFormattedDate, DateObjectType, convertToUpperCase } from '../../../../OffersSearchForm/utils/DateUtils';
import { getUrlParam } from '../../../../OffersSearchForm/utils/OfferUtils';

import { FLEXIBLE_FORMAT_YEAR_MONTH } from '../../../constants/ApplicationConstants';
import CommonUtils from '../../../utils/CommonUtils';

import { SearchMobileProps } from './SearchMobile.types';
import SearchMobileModalLazy from './SearchMobileModalLazy';
import { StyledSearchMobile } from './SearchMobile.styles';
import { ADD_BUDDHIST_YEAR_VALUE } from '../../../../../constants/CommonConstants';

const SearchMobile: FC<SearchMobileProps> = ({
  className,
  isHideDestinationProps = false,
  isPublishUserSelection = false,
  isHideFlexibleDates = false,
  isMPO = false,
  isSPO = false,
  previewMode,
  acceptLanguage,
  colorScheme,
}) => {
  const {
    mobilePlaceholderText,
    dateFormat,
    requestId,
    acceptLang,
    night,
    nights,
    title,
    dates,
    destination,
    offerId,
    flexibleDateFormatLabel,
  } = useContext(ContentContext);

  // to get destination name and get it fixed on mobile view for SPO
  const { UUID, formValues, setFormValues } = useContext(FormValuesContext);
  const propertyName = formValues?.[DESTINATION]?.displayText;
  const { fromDate, toDate, flexible } = formValues?.[DATES] ?? {};
  const globalData = usePersistentGlobalStore((state: any) => state.globalData);
  const ariesOffer = globalData?.sessionData?.cacheData?.data?.AriesOffer ?? {};
  const AriesSearch = globalData?.sessionData?.cacheData?.data?.AriesSearch ?? {};
  console.log('Search SearchMobile.tsx AriesSearch val', AriesSearch);
  console.log('Search SearchMobile.tsx AriesSearch val', JSON.stringify(AriesSearch));
  // const mpoOffersFlexibleDate = JSON.parse(window.sessionStorage.getItem('mpoOffersFlexibleDates') ?? 'false');
  const mpoOffersFlexibleDate = AriesSearch?.searchCriteria?.availabilityRequestVO?.flexibleDate;
  console.log('Search session SearchMobile.tsx mpoOffersFlexibleDate val', mpoOffersFlexibleDate);

  const formatMonthYear = (date: any) =>
    convertToUpperCase(
      getFormattedLocalizedDate(
        date,
        isGreaterChinaLocale(acceptLanguage)
          ? CN_FLEXIBLE_FORMAT_YEAR_MONTH
          : flexibleDateFormatLabel || FLEXIBLE_FORMAT_YEAR_MONTH,
        acceptLanguage
      ),
      acceptLanguage as string
    );

  let displayTextDate: any = '';
  if (fromDate || toDate) {
    displayTextDate = flexible
      ? formatMonthYear(acceptLanguage == 'th-TH' ? moment(fromDate)?.add(ADD_BUDDHIST_YEAR_VALUE, 'years') : fromDate)
      : `${getFormattedLocalizedDate(fromDate, dateFormat, acceptLanguage)}${
          toDate ? ` - ${getFormattedLocalizedDate(toDate, dateFormat, acceptLanguage)}` : ''
        }`;
  }
  //Update destination when offerId is same otherwise set with formvalues
  const destinationText =
    ariesOffer?.searchData?.offerId === offerId
      ? ariesOffer?.searchData?.address || formValues?.[DESTINATION]?.displayText
      : formValues?.[DESTINATION]?.displayText;
  const numberOfNights = CommonUtils.getNumberOfNights(fromDate, toDate);
  const flexibleLabel = useContext(ContentContext)['flexible'];
  let labelText = dates ? dates : '';
  if (numberOfNights > 0) {
    labelText = `${numberOfNights} ${numberOfNights > 1 ? nights : night}${flexible ? ` (${flexibleLabel})` : ''}`;
  }
  const hiddenFieldsValues = formValues?.[DESTINATION_HIDDEN_FIELDS];
  const isServer = !(typeof window != 'undefined' && window.document);

  //numberInParty is total number of persons.
  const childCount = formValues?.roomsAndGuests?.childrenCount;
  const numberInParty =
    (formValues?.roomsAndGuests?.adultsCount ? formValues.roomsAndGuests.adultsCount : 0) + (childCount ?? 0);

  /* states */
  const [dialogOpenState, setDialogOpenState] = useState(false);
  const [searchState, setSearchState] = useState('');
  const [onloadInput, setOnloadInput] = useState<string>(isSPO ? propertyName || '' : '');
  const [recentPId, setRecentPid] = useState('');
  const [timerList, setTimerList] = useState<ReturnType<typeof setTimeout>[]>([]);
  const isUrlWithPage = !isServer ? new URLSearchParams(window?.location?.search)?.has('pg') : false;

  //get onloadInput value
  useEffect(() => {
    const urlParamVal = getUrlParam();
    const inputValue = isSPO ? (urlParamVal !== '' ? urlParamVal : propertyName || '') : urlParamVal;
    setOnloadInput(inputValue);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const processSuggestionListdata = (suggestionListData: any) => {
    if (suggestionListData?.suggestedPlaces?.total > 0) {
      setRecentPid(suggestionListData.suggestedPlaces.edges?.[0]?.node?.placeId);
    }
  };

  const [getSuggestionList, { data: _suggestionListData, error: _suggestionListError }] = useLazyQuery(
    phoenixOfferSuggestedPlacesQuery,
    {
      fetchPolicy: isServer ? 'network-only' : 'cache-first',
      errorPolicy: UXL_ERROR_POLICY,
      context: {
        headers: {
          'x-request-id': requestId + '-o-offers-searchform',
          'accept-language': acceptLang,
        },
      },
      onCompleted: processSuggestionListdata,
    }
  );

  const clearTimer = (timers: ReturnType<typeof setTimeout>[]): void => {
    if (timers.length) {
      for (let i = timers.length - 1; i >= 0; i--) {
        clearTimeout(timers[i]);
      }
      setTimerList([]);
    }
  };
  useEffect(() => {
    updateFormValues(onloadInput, recentPId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentPId]);

  const updateFormValues = (val: string, pid?: string): void => {
    setFormValues(
      setDestination({
        displayText: val,
        pid,
        clusterCode: formValues?.destination?.clusterCode ?? '',
      })
    );
  };

  useEffect(() => {
    // Delaying API call till the input value finishes updating
    if (onloadInput) {
      clearTimer(timerList);
      const timer = setTimeout(() => {
        if (UUID) {
          // fetchSearchResults(onloadInput, UUID);
          getSuggestionList({
            variables: {
              query: onloadInput,
            },
          });
        }
      }, 500);
      setTimerList([...timerList, timer]);
    } else {
      // cancel current fetch timeout tiemr if user delete the input value
      clearTimer(timerList);
      // clear options list and show recently viewed when input value is cleared
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onloadInput]);

  useEffect(() => {
    const searchFormData = {
      latitude: hiddenFieldsValues?.latitude ?? '',
      longitude: hiddenFieldsValues?.longitude ?? '',
      stateCode: hiddenFieldsValues?.state ?? '',
      countryCode: hiddenFieldsValues?.country ?? '',
      startDate: getMPOFormattedDate(),
      endDate: getMPOFormattedDate(),
      quantity: formValues?.roomsAndGuests?.roomsCount,
      numberInParty: numberInParty,
      childAges: formValues?.roomsAndGuests?.childrenAges,
      flexible: formValues?.dates?.flexible === null ? null : 'on',
    };
    //call eventutil only if the location data is available to pass over to properties service, as onloadInput will set to '' after execution.
    if (onloadInput && (hiddenFieldsValues?.country || hiddenFieldsValues?.placeId)) {
      setFormValues(
        setDates({
          ...(formValues?.[DATES] ?? {}),
          flexible: 'on',
        })
      );
      window.sessionStorage.setItem('mpoOffersFlexibleDates', 'true'); // On initial load, set mpoOffersFlexibleDates to true if it's a deeplink. This will be used by the PropertiesList component.
      eventUtil.dispatch('DeepLinkUtil', { searchFormData });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues?.[DESTINATION_HIDDEN_FIELDS]?.country, formValues?.[DESTINATION_HIDDEN_FIELDS]?.placeId]);

  /* event handlers */
  // set form form state on dialog open
  const openDialog = (state: string): void => {
    setSearchState(state);
    setDialogOpenState(true);
  };

  return (
    <>
      <div className="mpo-search-title">
        {isMPO && <Text copyText={title} fontSize={Types.size.medium} element={Types.tags.paragraph} />}
      </div>
      <StyledSearchMobile className={`${className} container m-search-container ${previewMode ? 'preview-cta' : ''}`}>
        {!isHideDestinationProps && isMPO ? (
          <button className="search-primary-destination" onClick={(): void => openDialog(DESTINATION_STATE)}>
            {/* for sticky state, show only icon */}
            <span className="icon-location" />
            {/* for non sticky state, show icon and label */}
            <span className="t-font-xs icon-location">{destination}</span>
            <span className="t-subtitle-m">
              {' '}
              {
                // Showing right destination once selected destination when traversed pagination and comeback on page 1
                (onloadInput && !isUrlWithPage && !ariesOffer?.searchData?.address
                  ? onloadInput
                  : destinationText
                  ? destinationText
                  : mobilePlaceholderText) as string
              }
            </span>
          </button>
        ) : (
          !isHideDestinationProps &&
          !isMPO && (
            <button className="search-primary-destination">
              {/* for sticky state, show only icon */}
              <span className="icon-location" />
              {/* for non sticky state, show icon and label */}
              <span className="t-font-xs icon-location">{destination}</span>
              <span className="t-subtitle-m">{propertyName as string}</span>
            </button>
          )
        )}
        <button
          onClick={(): void => openDialog(CALENDAR_STATE)}
          className={`${isHideDestinationProps ? `no-padding` : ``}`}
        >
          {/* for sticky state, show only icon */}
          <Text copyText={''} customClass={'icon-nav---book'} fontSize={undefined} element={Types.tags.span} />
          {/* for non sticky state, show icon and label */}
          <Text
            copyText={labelText}
            customClass={'t-font-xs icon-nav---book'}
            fontSize={undefined}
            element={Types.tags.span}
          />
          <Text
            copyText={displayTextDate}
            customClass={'t-subtitle-inverse-l search-date-label'}
            fontSize={undefined}
            element={Types.tags.span}
          />
        </button>
        {!dialogOpenState ? (
          ''
        ) : (
          <SearchMobileModalLazy
            searchState={searchState}
            setSearchState={setSearchState}
            setDialogOpenState={setDialogOpenState}
            dialogOpenState={dialogOpenState}
            isHideDestinationProps={isHideDestinationProps}
            isPublishUserSelection={isPublishUserSelection}
            isHideFlexibleDates={isHideFlexibleDates}
            isMPO={isMPO}
            isSPO={isSPO}
            acceptLanguage={acceptLanguage}
            colorScheme={colorScheme}
          />
        )}
      </StyledSearchMobile>
    </>
  );
};

export default SearchMobile;
